import React, { useCallback, useState } from 'react';
import { Button } from '@amzn/awsui-components-react';
import AIInsightPanel from './AIInsightPanel';
import { useAIInsightsContext } from './AIInsightsContext';
import './AIInsight.scss';

export interface AIInsightButtonProps {
    /** Calculation line item the conversation is scoped to. May be empty for unscoped chats. */
    cliId: string;
    /** Report type passed to the agent as context, e.g. TP_TAX or VAR. */
    reportType?: string;
    /** Opens the panel on mount instead of behind a trigger button. */
    autoOpen?: boolean;
    /** Renders the trigger as an icon-only button, for use inside table cells. */
    iconOnly?: boolean;
    /** Renders the panel in the page flow rather than in a modal. */
    inline?: boolean;
    accountingPeriod?: string;
    accountingDay?: string;
    /** Prompt sent automatically when the panel is opened. Shown in the chat as the user's message. */
    defaultPrompt?: string;
}

/**
 * Trigger for the AI Insights conversation.
 *
 * Holds no chat state: the conversation lives in AIInsightPanel, which is mounted only once
 * opened. `enabled` and the signed-in user come from AIInsightsContext, so neither the flag
 * nor userProfile has to be resolved or threaded by each call site.
 */
export default function AIInsightButton({
    cliId,
    reportType,
    autoOpen,
    iconOnly,
    inline,
    accountingPeriod,
    accountingDay,
    defaultPrompt,
}: AIInsightButtonProps) {
    const { enabled } = useAIInsightsContext();
    const [showPanel, setShowPanel] = useState(!!autoOpen);

    const openPanel = useCallback(() => setShowPanel(true), []);
    const closePanel = useCallback(() => setShowPanel(false), []);

    if (!enabled) {
        return null;
    }

    const heading = cliId ? `AI Insights — ${cliId}` : 'AI Insights';

    const panel = showPanel && (
        <AIInsightPanel
            context={{ cliId, reportType, accountingPeriod, accountingDay }}
            heading={heading}
            inline={inline}
            defaultPrompt={defaultPrompt}
            onClose={closePanel}
        />
    );

    // Inline panels replace the trigger; modal panels sit alongside it.
    if (inline) {
        return panel || (
            <Button variant="primary" iconName="gen-ai" onClick={openPanel}>
                AI Insight
            </Button>
        );
    }

    const trigger = iconOnly ? (
        <Button
            variant="inline-icon"
            iconName="gen-ai"
            ariaLabel={`Open AI Insights for ${cliId || 'this record'}`}
            onClick={openPanel}
        />
    ) : (
        <Button variant="primary" iconName="gen-ai" onClick={openPanel}>
            AI Insight
        </Button>
    );

    return (
        <>
            {trigger}
            {panel}
        </>
    );
}
